<template>
  <div class="pages-view">
    <header class="pages-view__header">
      <div class="pages-view__header-top">
        <div>
          <h2 class="pages-view__title">Pages</h2>
          <p class="pages-view__subtitle">Manage your website's structural content</p>
        </div>
        <router-link :to="{ name: 'AddPage' }" class="pages-view__btn-create">
          <span class="material-symbols-outlined">add</span>
          Create New Page
        </router-link>
      </div>
      <div class="pages-view__toolbar">
        <div class="pages-view__search-wrap">
          <span class="material-symbols-outlined pages-view__search-icon">search</span>
          <input
            v-model="searchQuery"
            type="text"
            class="pages-view__search"
            placeholder="Search pages..."
          >
        </div>
      </div>
    </header>

    <div class="pages-view__body">
      <div class="pages-view__card">
        <template v-if="loading">
          <div class="pages-view__empty">Učitavanje…</div>
        </template>
        <template v-else-if="!filteredPages.length">
          <div class="pages-view__empty">Ni jedna stranica nije pronadjena.</div>
        </template>
        <template v-else>
          <table class="pages-view__table">
            <thead>
              <tr>
                <th class="pages-view__th">Page Details</th>
                <th class="pages-view__th">Path</th>
                <th class="pages-view__th">Status</th>
                <th class="pages-view__th pages-view__th--right">Actions</th>
              </tr>
            </thead>
            <tbody class="pages-view__tbody">
              <tr
                v-for="(page, index) in paginatedPages"
                :key="uniquePageKey(page, index)"
                class="pages-view__row"
              >
                <td class="pages-view__td">
                  <p class="pages-view__page-title">{{ pageTitle(page) }}</p>
                </td>
                <td class="pages-view__td">
                  <code class="pages-view__path">{{ pathDisplay(page) }}</code>
                </td>
                <td class="pages-view__td">
                  <span class="pages-view__status pages-view__status--published">
                    <span class="pages-view__status-dot" />
                    Published
                  </span>
                </td>
                <td class="pages-view__td pages-view__td--right">
                  <div class="pages-view__row-actions">
                    <router-link
                      v-if="canEditPage(page)"
                      :to="{ name: 'EditPage', params: { id: page.id } }"
                      class="pages-view__action"
                      title="Edit"
                    >
                      <span class="material-symbols-outlined">edit_note</span>
                    </router-link>
                    <a
                      :href="pageUrl(page)"
                      target="_blank"
                      rel="noopener"
                      class="pages-view__action"
                      title="Preview"
                    >
                      <span class="material-symbols-outlined">open_in_new</span>
                    </a>
                    <button
                      v-if="canEditPage(page)"
                      type="button"
                      class="pages-view__action pages-view__action--danger"
                      title="Delete"
                      @click="remove(page.id)"
                    >
                      <span class="material-symbols-outlined">delete</span>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>

          <div class="pages-view__footer">
          <p class="pages-view__footer-text">
            Showing <span class="pages-view__footer-num">{{ rangeStart }}-{{ rangeEnd }}</span>
            of <span class="pages-view__footer-num">{{ filteredPages.length }}</span> results
          </p>
          <div class="pages-view__pagination">
            <button
              type="button"
              class="pages-view__page-btn"
              :disabled="currentPage <= 1"
              @click="currentPage = currentPage - 1"
            >
              <span class="material-symbols-outlined">chevron_left</span>
            </button>
            <button
              v-for="n in pageNumbers"
              :key="n"
              type="button"
              class="pages-view__page-btn"
              :class="{ 'pages-view__page-btn--active': n === currentPage }"
              @click="currentPage = n"
            >
              {{ n }}
            </button>
            <button
              type="button"
              class="pages-view__page-btn"
              :disabled="currentPage >= totalPages"
              @click="currentPage = currentPage + 1"
            >
              <span class="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { Message, MessageBox } from 'element-ui'

const PER_PAGE = 10

/** About Us, Projects, Contact, Gallery: show only Preview. All other pages get Edit + Delete. */
const PREVIEW_ONLY_PATHS = ['onama', 'projekti', 'kontakt', 'galerija']

export default {
  name: 'Pages',

  data () {
    return {
      data: [],
      loading: true,
      searchQuery: '',
      currentPage: 1
    }
  },

  computed: {
    filteredPages () {
      const list = Array.isArray(this.data) ? this.data : []
      const q = (this.searchQuery || '').trim().toLowerCase()
      if (!q) return list
      return list.filter(page => {
        const title = this.pageTitle(page).toLowerCase()
        const meta = this.pageMeta(page).toLowerCase()
        const path = this.pathDisplay(page).toLowerCase()
        return title.includes(q) || meta.includes(q) || path.includes(q)
      })
    },

    totalPages () {
      return Math.max(1, Math.ceil(this.filteredPages.length / PER_PAGE))
    },

    pageNumbers () {
      const total = this.totalPages
      const current = this.currentPage
      const pages = []
      let start = Math.max(1, current - 1)
      let end = Math.min(total, start + 2)
      if (end - start < 2) {
        start = Math.max(1, end - 2)
      }
      for (let i = start; i <= end; i++) pages.push(i)
      return pages
    },

    paginatedPages () {
      const start = (this.currentPage - 1) * PER_PAGE
      return this.filteredPages.slice(start, start + PER_PAGE)
    },

    rangeStart () {
      if (!this.filteredPages.length) return 0
      return (this.currentPage - 1) * PER_PAGE + 1
    },

    rangeEnd () {
      return Math.min(this.currentPage * PER_PAGE, this.filteredPages.length)
    }
  },

  watch: {
    searchQuery () {
      this.currentPage = 1
    }
  },

  beforeRouteEnter (to, from, next) {
    next(vm => {
      vm.loading = true
      const store = vm.$store
      axios.get('/api/pages')
        .then(({ data }) => {
          const list = Array.isArray(data.data) ? data.data : []
          const created = store.state.pages.createdPage
          let combined
          if (created && created.id != null) {
            const exists = list.some(p => p.id === created.id)
            combined = exists ? list : [created, ...list]
            store.commit('pages/setCreatedPage', null)
          } else {
            combined = list
          }
          vm.data = vm.dedupePagesById(combined)
        })
        .catch(() => {
          const created = store.state.pages.createdPage
          if (created && created.id != null) {
            vm.data = [created]
            store.commit('pages/setCreatedPage', null)
            Message.success('Stranica je sačuvana. Lista nije učitana sa servera.')
          } else {
            vm.data = []
            Message.error('Nije moguće učitati listu stranica. Proverite konekciju i API.')
          }
        })
        .finally(() => {
          vm.loading = false
        })
    })
  },

  methods: {
    dedupePagesById (pages) {
      const seen = new Set()
      return (pages || []).filter(p => {
        const id = p.id != null ? String(p.id) : null
        if (id === null) return true
        if (seen.has(id)) return false
        seen.add(id)
        return true
      })
    },

    uniquePageKey (page, index) {
      const id = page.id != null ? String(page.id) : null
      return id != null ? `page-${id}-${index}` : `page-noid-${index}`
    },

    pageTitle (page) {
      if (page.data && page.data.rs && page.data.rs.title) return page.data.rs.title
      if (page.data && page.data.en && page.data.en.title) return page.data.en.title
      return 'Untitled'
    },

    pageMeta (page) {
      if (page.data && page.data.rs && page.data.rs.meta) return page.data.rs.meta
      return ''
    },

    pathDisplay (page) {
      if (page.url) return `/${page.url}`
      return '/'
    },

    canEditPage (page) {
      const url = (page.url || '').trim().toLowerCase()
      return !PREVIEW_ONLY_PATHS.includes(url)
    },

    pageUrl (page) {
      const frontendBase = process.env.NODE_ENV === 'development' && process.env.VUE_APP_LOCAL_FRONTEND
        ? process.env.VUE_APP_LOCAL_FRONTEND
        : 'https://mondial90.rs'
      return frontendBase + this.pathDisplay(page)
    },

    async remove (id) {
      try {
        await MessageBox.confirm('Da li si siguran da želiš da obrišeš ovu stranu?', 'Upozorenje!', {
          confirmButtonText: 'Obriši',
          cancelButtonText: 'Odustani',
          type: 'error'
        })
        await axios.delete(`/api/page/${id}`)
        const idx = this.data.findIndex(page => page.id === id)
        if (idx !== -1) this.data.splice(idx, 1)
        Message.success('Stranica uspešno obrisana.')
      } catch {}
    }
  }
}
</script>

<style lang="scss" scoped>
.pages-view {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  font-family: 'Manrope', sans-serif;
}

.pages-view__header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(24px);
  padding: 2rem 3rem;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  border-bottom: 1px solid #f1f5f9;
}

.pages-view__header-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.pages-view__title {
  font-size: 2.25rem;
  font-weight: 800;
  letter-spacing: -0.025em;
  color: #0f172a;
  margin: 0;
}

.pages-view__subtitle {
  font-size: 0.875rem;
  color: #94a3b8;
  margin: 0.25rem 0 0;
}

.pages-view__btn-create {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 3rem;
  padding: 0.75rem 1.5rem;
  box-sizing: border-box;
  background: #1a1a1a;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  border-radius: 9999px;
  text-decoration: none;
  transition: background 0.15s;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  .material-symbols-outlined {
    font-size: 0.875rem;
  }
  &:hover {
    background: #334155;
  }
}

.pages-view__toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.pages-view__search-wrap {
  flex: 1;
  max-width: 32rem;
  position: relative;
}

.pages-view__search-icon {
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  font-size: 1.25rem;
  color: #94a3b8;
  pointer-events: none;
}

.pages-view__search {
  width: 100%;
  padding: 0.75rem 1rem 0.75rem 3rem;
  font-size: 0.875rem;
  border: none;
  border-radius: 1rem;
  background: rgba(241, 245, 249, 0.5);
  color: #0f172a;
  outline: none;
  transition: box-shadow 0.15s;
  &::placeholder {
    color: #94a3b8;
  }
  &:focus {
    box-shadow: 0 0 0 2px #e2e8f0;
  }
}

.pages-view__body {
  flex: 1;
  overflow-y: auto;
  padding: 2rem 3rem 3rem;
}

.pages-view__card {
  background: #fff;
  border-radius: 2rem;
  border: 1px solid #f1f5f9;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  position: relative;
}

.pages-view__table {
  width: 100%;
  border-collapse: collapse;
  text-align: left;
}

.pages-view__th {
  padding: 1.25rem 1.5rem;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: #94a3b8;
  border-bottom: 1px solid #f1f5f9;
  &.pages-view__th--right {
    text-align: right;
  }
}

.pages-view__tbody {
  border-collapse: collapse;
}

.pages-view__row {
  transition: background 0.15s;
  &:hover {
    background: rgba(248, 250, 252, 0.5);
  }
}

.pages-view__td {
  padding: 1.5rem 1.5rem;
  border-bottom: 1px solid #f8fafc;
  vertical-align: middle;
  &.pages-view__td--right {
    text-align: right;
  }
}

.pages-view__page-title {
  font-size: 1rem;
  font-weight: 700;
  color: #0f172a;
  margin: 0;
}

.pages-view__path {
  font-size: 0.875rem;
  font-weight: 500;
  color: #64748b;
  background: #f8fafc;
  padding: 0.25rem 0.5rem;
  border-radius: 0.375rem;
}

.pages-view__status {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 600;
  border: 1px solid rgba(16, 185, 129, 0.2);
  background: #ecfdf5;
  color: #059669;
  &--published {
    .pages-view__status-dot {
      background: #10b981;
    }
  }
}

.pages-view__status-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  margin-right: 0.5rem;
  flex-shrink: 0;
}

.pages-view__row-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}

.pages-view__action {
  display: inline-flex;
  padding: 0.5rem;
  color: #64748b;
  border-radius: 0.75rem;
  transition: all 0.15s;
  .material-symbols-outlined {
    font-size: 1.25rem;
  }
  &:hover {
    background: rgba(226, 232, 240, 0.5);
    color: #0f172a;
  }
  &--danger:hover {
    background: #fef2f2;
    color: #dc2626;
  }
}

button.pages-view__action {
  border: none;
  background: none;
  cursor: pointer;
}

.pages-view__empty {
  padding: 3rem 2rem;
  text-align: center;
  color: #64748b;
  font-size: 0.875rem;
}

.pages-view__footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.5rem 2rem;
  background: rgba(248, 250, 252, 0.3);
  border-top: 1px solid #f1f5f9;
}

.pages-view__footer-text {
  font-size: 13px;
  color: #94a3b8;
  font-weight: 500;
  margin: 0;
}

.pages-view__footer-num {
  color: #0f172a;
}

.pages-view__pagination {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.pages-view__page-btn {
  width: 2.5rem;
  height: 2.5rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 600;
  color: #64748b;
  background: transparent;
  border: none;
  border-radius: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  .material-symbols-outlined {
    font-size: 1.25rem;
  }
  &:hover:not(:disabled) {
    background: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
    border: 1px solid #f1f5f9;
    color: #475569;
  }
  &:disabled {
    color: #cbd5e1;
    cursor: default;
  }
  &--active {
    background: #1a1a1a;
    color: #fff;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
  }
}
</style>
